import { useState, useRef, useEffect } from "react";
import { Phone, PhoneOff, PhoneIncoming, ChevronUp, X } from "lucide-react";
import { useActiveCall } from "@/contexts/ActiveCallContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { getInitials } from "@/lib/avatarUtils";

/**
 * PendingCallBanner — Mostra chamadas pendentes enquanto o agente já está em ligação.
 * - 1 chamada: card completo (visual atual)
 * - 2+ chamadas: badge compacto com dropdown listando todas
 */
export function PendingCallBanner() {
  const { pendingCalls, activeCall, switchToCall, dismissPendingCall } =
    useActiveCall();

  if (!pendingCalls || pendingCalls.length === 0) return null;

  const hasActiveCall = !!activeCall;
  const activeIsRinging = activeCall?.status === "RINGING";

  // 1 chamada → card completo
  if (pendingCalls.length === 1) {
    return (
      <div className="fixed z-[9998] bottom-44 right-6 animate-in slide-in-from-bottom-2 fade-in duration-300">
        <PendingCallCard
          call={pendingCalls[0]}
          hasActiveCall={hasActiveCall}
          activeIsRinging={activeIsRinging}
          onSwitch={() => switchToCall(pendingCalls[0])}
          onDismiss={() => dismissPendingCall(pendingCalls[0].callId)}
        />
      </div>
    );
  }

  // 2+ chamadas → badge + dropdown
  return (
    <PendingCallStack
      calls={pendingCalls}
      hasActiveCall={hasActiveCall}
      activeIsRinging={activeIsRinging}
      onSwitch={switchToCall}
      onDismiss={dismissPendingCall}
    />
  );
}


function PendingCallStack({ calls, hasActiveCall, activeIsRinging, onSwitch, onDismiss }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Fechar ao clicar fora
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div
      ref={containerRef}
      className="fixed z-[9998] bottom-44 right-6 animate-in slide-in-from-bottom-2 fade-in duration-300"
    >
      {/* Dropdown lista */}
      {open && (
        <div className="mb-2 bg-surface border border-border rounded-xl shadow-2xl shadow-black/50 overflow-hidden max-h-[320px] overflow-y-auto w-[320px]">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <span className="text-xs font-medium text-foreground-muted">
              {calls.length} chamadas aguardando
            </span>
            <button
              onClick={() => setOpen(false)}
              className="text-foreground-muted hover:text-foreground p-0.5 rounded"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {calls.map((call) => (
            <PendingCallRow
              key={call.callId}
              call={call}
              hasActiveCall={hasActiveCall}
              activeIsRinging={activeIsRinging}
              onSwitch={() => {
                onSwitch(call);
                setOpen(false);
              }}
              onDismiss={() => onDismiss(call.callId)}
            />
          ))}
        </div>
      )}

      {/* Badge compacto */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2.5 rounded-xl shadow-lg shadow-black/30 transition-colors w-full"
      >
        <div className="relative">
          <PhoneIncoming className="h-4 w-4" />
          <span className="absolute -top-1.5 -right-2 h-4 w-4 flex items-center justify-center rounded-full bg-white text-amber-700 text-[10px] font-bold">
            {calls.length}
          </span>
        </div>
        <span className="text-sm font-medium ml-1">
          {calls.length} chamadas aguardando
        </span>
        <ChevronUp
          className={`h-4 w-4 ml-auto transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
    </div>
  );
}

function PendingCallRow({ call, hasActiveCall, activeIsRinging, onSwitch, onDismiss }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 hover:bg-surface-hover border-b border-border last:border-b-0">
      {/* Avatar */}
      <div className="relative shrink-0">
        <Avatar className="h-8 w-8 border border-amber-500">
          <AvatarImage src={call.contactAvatar} alt={call.contactName} />
          <AvatarFallback className="bg-surface-active text-foreground-secondary text-[10px]">
            {getInitials(call.contactName, "?")}
          </AvatarFallback>
        </Avatar>
        <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-amber-500 animate-pulse" />
      </div>

      {/* Nome */}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-foreground-strong truncate">
          {call.contactName}
        </p>
        <span className="text-[10px] text-amber-700 dark:text-amber-400">
          Chamada recebida
        </span>
      </div>

      {/* Ações compactas */}
      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={onDismiss}
          className="p-1.5 rounded-lg text-foreground-muted hover:text-red-400 hover:bg-red-500/10 transition-colors"
          title="Ignorar"
        >
          <PhoneOff className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={onSwitch}
          className="p-1.5 rounded-lg text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 transition-colors"
          title={!hasActiveCall ? "Atender" : activeIsRinging ? "Atender esta" : "Encerrar e atender"}
        >
          <Phone className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function PendingCallCard({ call, hasActiveCall, activeIsRinging, onSwitch, onDismiss }) {
  return (
    <div className="bg-surface border border-amber-700/50 rounded-xl shadow-xl shadow-black/40 p-3 min-w-[280px] max-w-[320px]">
      <div className="flex items-center gap-3">
        {/* Avatar com pulse */}
        <div className="relative">
          <Avatar className="h-9 w-9 border-2 border-amber-500">
            <AvatarImage src={call.contactAvatar} alt={call.contactName} />
            <AvatarFallback className="bg-surface-active text-foreground-secondary text-xs">
              {getInitials(call.contactName, "?")}
            </AvatarFallback>
          </Avatar>
          <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-amber-500 animate-pulse" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground-strong truncate">
            {call.contactName}
          </p>
          <div className="flex items-center gap-1">
            <PhoneIncoming className="h-3 w-3 text-amber-700 dark:text-amber-400" />
            <span className="text-xs text-amber-700 dark:text-amber-400">
              Chamada recebida
            </span>
          </div>
        </div>
      </div>

      {/* Ações */}
      <div className="flex items-center gap-2 mt-2.5">
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className="flex-1 h-8 text-xs text-foreground-muted hover:text-foreground hover:bg-surface-hover rounded-lg"
        >
          <PhoneOff className="h-3.5 w-3.5 mr-1.5" />
          Ignorar
        </Button>
        <Button
          size="sm"
          onClick={onSwitch}
          className="flex-1 h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
        >
          <Phone className="h-3.5 w-3.5 mr-1.5" />
          {!hasActiveCall ? "Atender" : activeIsRinging ? "Atender esta" : "Encerrar e atender"}
        </Button>
      </div>
    </div>
  );
}
